import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuthStore } from '@stores/auth-store';

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  // Derive authentication directly from user presence — never from a
  // persisted boolean that can be stale or missing on first reload.
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const location = useLocation();

  // A user is authenticated if and only if their profile is in the store.
  // SessionRestoreProvider keeps isLoading=true while restoring the access
  // token, so this guard always shows a spinner instead of the login page
  // during that window.
  const isAuthenticated = user !== null;

  if (isLoading) {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label="Verifying session"
        className="erp-auth-loader"
      >
        <span className="sr-only">Verifying session…</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the intended path so login can redirect back after success
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
